import { DataTable, FAIcon } from '@/components'
import { DepartmentCombobox } from '@/components/combobox/department-combox'
import { FacilityCombobox } from '@/components/combobox/facility-combobox'
import { RegularModal } from '@/components_deprecated'
import { format } from '@/utils'
import { createColumnHelper } from '@tanstack/react-table'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { useMemo, useState } from 'react'
import {
  buildGroupedActivityRows,
  filterGroupedActivityRows,
  UNASSIGNED,
  type DistributionWorkPeriodGroup,
  type GroupedActivityRow,
} from '../utils/grouped-activity-distribution'

interface GroupedActivityDistributionModalProps {
  onClose: PromptResolve<boolean | null>
  /** The month report's work period groups, which carry the grouped activities. */
  workPeriodGroups: readonly DistributionWorkPeriodGroup[]
}

const column = createColumnHelper<GroupedActivityRow>()

/**
 * Every block of activities behind the month's salary codes, with the
 * department and facility each one is attributed to.
 *
 * The salary code table totals a code over the whole month. This is the same
 * hours unrolled: one row per stretch of a shift on one vehicle under one
 * salary code, so an attribution that looks wrong can be traced to the day and
 * the vehicle that produced it. A month of shifts runs to hundreds of rows, so
 * the table paginates.
 */
export const GroupedActivityDistributionModal: React.FC<
  GroupedActivityDistributionModalProps
> = ({ onClose, workPeriodGroups }) => {
  const translations = useTranslate({
    title: 'salary.department-distribution-title',
    description: 'salary.department-distribution-description',
    empty: 'salary.department-distribution-empty',
    noMatches: 'salary.department-distribution-no-matches',
    nRows: ['salary.department-distribution-n-rows', { n: 0 }],
    date: 'common.date',
    time: 'common.time',
    salaryCode: 'common.salary-code',
    department: 'common.department',
    facility: 'common.facility',
    vehicle: 'common.vehicle',
    amount: 'common.amount',
    close: 'common.close',
    allDepartments: 'salary.department-distribution-all-departments',
    allFacilities: 'salary.department-distribution-all-facilities',
    noDepartment: 'salary.department-distribution-no-department',
    noFacility: 'salary.department-distribution-no-facility',
  })

  const [department, setDepartment] = useState('')
  const [facility, setFacility] = useState('')

  const rows = useMemo(
    () => buildGroupedActivityRows(workPeriodGroups),
    [workPeriodGroups]
  )

  const filteredRows = useMemo(
    () => filterGroupedActivityRows(rows, { department, facility }),
    [department, facility, rows]
  )

  const columns = useMemo(
    () => [
      column.accessor('start', {
        id: 'date',
        header: translations.date,
        enableSorting: true,
        cell: info => format(new Date(info.getValue()), 'dd.MM.yyyy'),
      }),
      column.display({
        id: 'time',
        header: translations.time,
        cell(info) {
          const row = info.row.original
          return `${format(new Date(row.start), 'HH:mm')} – ${format(
            new Date(row.end),
            'HH:mm'
          )}`
        },
      }),
      column.accessor('salaryCode', {
        header: translations.salaryCode,
        enableSorting: true,
      }),
      column.accessor('department', {
        header: translations.department,
        enableSorting: true,
      }),
      column.accessor('facility', {
        header: translations.facility,
        enableSorting: true,
      }),
      column.accessor('vehicle', {
        header: translations.vehicle,
        enableSorting: true,
      }),
      column.accessor('amount', {
        header: translations.amount,
        meta: { end: true },
      }),
    ],
    [
      translations.amount,
      translations.date,
      translations.department,
      translations.facility,
      translations.salaryCode,
      translations.time,
      translations.vehicle,
    ]
  )

  return (
    <RegularModal
      width="960px"
      cancelText={translations.close}
      onCancel={() => onClose(null)}
      cancelOnClickOutside
      overflowY="auto"
    >
      <div>
        <div className="mb-1 flex items-start justify-between gap-4">
          <h2 className="text-lg font-semibold text-stone-900">
            {translations.title}
          </h2>
          <button
            type="button"
            className="text-stone-400 transition-colors hover:text-stone-600"
            aria-label={translations.close}
            onClick={() => onClose(null)}
          >
            <FAIcon icon="times" />
          </button>
        </div>

        <p className="mb-4 text-sm text-stone-500">
          {translations.description}
        </p>

        <div className="mb-4 grid gap-3 sm:grid-cols-2">
          <div>
            <label
              className="mb-1 ml-1 block text-sm font-medium text-stone-700"
              htmlFor="distribution-department"
            >
              {translations.department}
            </label>
            <DepartmentCombobox
              id="distribution-department"
              mode="single"
              clearable
              value={department}
              placeholder={translations.allDepartments}
              // Heads the list, so the blocks with no department at all can be
              // picked out the same way as a real one.
              alwaysInclude={{
                id: UNASSIGNED,
                name: translations.noDepartment,
                externalId: '',
              }}
              onChange={setDepartment}
            />
          </div>
          <div>
            <label
              className="mb-1 ml-1 block text-sm font-medium text-stone-700"
              htmlFor="distribution-facility"
            >
              {translations.facility}
            </label>
            <FacilityCombobox
              id="distribution-facility"
              mode="single"
              clearable
              value={facility}
              placeholder={translations.allFacilities}
              alwaysInclude={{
                id: UNASSIGNED,
                name: translations.noFacility,
                externalId: '',
              }}
              onChange={setFacility}
            />
          </div>
        </div>

        <DataTable
          columns={columns}
          data={filteredRows}
          noDataText={
            rows.length === 0 ? translations.empty : translations.noMatches
          }
          pagination={{
            pageSize: 10,
            itemsText: n => translations.nRows({ n }),
          }}
        />
      </div>
    </RegularModal>
  )
}
